import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ImagePlus, FileText, X, Sparkles, ChevronLeft, Lock,
} from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useGuestGate } from "../lib/useGuestGate";
import { BRAND_COLORS } from "../lib/bookingStatus";

const IMAGE_ACCEPT = "image/*";
const DOC_ACCEPT   = "application/pdf,.doc,.docx,.xls,.xlsx,.txt";

const EMPTY = {
  title: "", description: "", property_id: "", priority: "normal",
  date_needed: "", preferred_time: "", budget: "",
  requires_approval: true, attachments: [],
};

export default function CustomerRequestNew() {
  const { lang } = useI18n();
  const de = lang === "de";
  const PRIORITIES = [
    { key: "normal", label: de ? "Normal" : "Normal" },
    { key: "high",   label: de ? "Hoch" : "High" },
    { key: "urgent", label: de ? "Dringend" : "Urgent" },
  ];
  const POPULAR_PROMPTS = de ? [
    "Handwerker für tropfenden Wasserhahn",
    "Privatkoch für Samstagabend",
    "Skiservice für vier Paar Ski",
    "Möbellieferung annehmen und aufbauen",
    "Fensterputzer vor der Anreise",
    "Bootsservice und Winterlager",
  ] : [
    "Handyman for a dripping tap",
    "Private chef for Saturday evening",
    "Ski servicing for four pairs of skis",
    "Furniture delivery, receive and assemble",
    "Window cleaner before arrival",
    "Boat service and winter storage",
  ];

  const nav = useNavigate();
  const gate = useGuestGate();
  const [properties, setProperties] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const imgRef = useRef(null);
  const docRef = useRef(null);

  useEffect(() => {
    (async () => {
      const p = await api.get("/properties");
      setProperties(p.data);
      if (p.data[0]) setForm((f) => ({ ...f, property_id: f.property_id || p.data[0].id }));
    })();
  }, []);

  const pickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (e.target) e.target.value = "";
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        try {
          const fd = new FormData();
          fd.append("file", file);
          const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setAttachments((prev) => [...prev, { id: data.id, name: file.name, is_image: file.type.startsWith("image/") }]);
        } catch (err) {
          toast.error(`${file.name}: ${formatErr(err.response?.data?.detail) || err.message}`);
        }
      }
    } finally { setUploading(false); }
  };
  const removeAttachment = (id) => setAttachments((prev) => prev.filter((a) => a.id !== id));

  const canSubmit = form.title.trim() && form.description.trim() && form.property_id && !sending;

  const submit = async (e) => {
    e?.preventDefault();
    if (gate.isGuest) { gate.promptUpgrade(); return; }
    if (!canSubmit) return;
    setSending(true);
    try {
      const payload = {
        ...form,
        title: form.title.trim(),
        description: form.description.trim(),
        attachments: attachments.map((a) => a.id),
        date_needed: form.date_needed || null,
        preferred_time: form.preferred_time || null,
        budget: form.budget || null,
      };
      await api.post("/requests", payload);
      toast.success(de ? "Anfrage gesendet." : "Request sent.");
      nav("/app/requests");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setSending(false); }
  };

  return (
    <div className="max-w-[720px] mx-auto px-5 md:px-8 py-8 md:py-12 pb-40" data-testid="customer-request-new">
      <div className="mb-6">
        <button
          type="button"
          onClick={() => nav("/app/requests")}
          data-testid="request-new-back-btn"
          className="inline-flex items-center gap-1.5 text-[12px] tracking-wider uppercase text-navy-dark/70 hover:text-navy mb-4"
        >
          <ChevronLeft className="w-4 h-4" strokeWidth={1.6} />
          {de ? "Zurück zur Übersicht" : "Back to overview"}
        </button>
        <div className="label-caps mb-3">
          {de ? "Sie buchen. Weissblom liefert." : "You ask. Weissblom delivers."}
        </div>
        <h1 className="font-display text-4xl md:text-5xl text-navy leading-tight">
          {de ? "Sonderwünsche" : "Request Anything"}
        </h1>
        <p className="text-[15px] text-navy-dark/70 mt-3 max-w-lg leading-relaxed">
          {de
            ? "Beschreiben Sie, was Sie brauchen. Wir koordinieren Handwerker, Termine und Lieferung."
            : "Describe what you need. We coordinate the tradespeople, appointments and delivery."}
        </p>
      </div>

      <form onSubmit={submit} className="bg-white border border-navy/10 p-5 md:p-7 space-y-6" data-testid="request-form">
        <div>
          <label htmlFor="req-title" className="block mb-2 text-[13px] font-medium text-navy-dark">
            {de ? "Titel" : "Title"}
          </label>
          <input
            id="req-title" data-testid="request-title-input"
            required value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder={de ? "z. B. Sauna prüfen lassen" : "e.g. Have the sauna serviced"}
            className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-navy-dark"
          />
        </div>

        <div>
          <label htmlFor="req-desc" className="block mb-2 text-[13px] font-medium text-navy-dark">
            {de ? "Ihr Anliegen" : "Your request"}
          </label>
          <textarea
            id="req-desc" data-testid="request-description-input"
            required rows={5} value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder={de
              ? "z. B. Sauna vor dem Wochenende reparieren lassen …"
              : "e.g. Have the sauna repaired before the weekend …"}
            className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-navy-dark leading-relaxed"
          />
        </div>

        <div>
          <label htmlFor="req-prop" className="block mb-2 text-[13px] font-medium text-navy-dark">
            {de ? "Objekt wählen" : "Choose property"}
          </label>
          <select
            id="req-prop" data-testid="request-property-select"
            value={form.property_id}
            onChange={(e) => setForm({ ...form, property_id: e.target.value })}
            className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-navy-dark"
          >
            {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>

        <div>
          <label htmlFor="req-prio" className="block mb-2 text-[13px] font-medium text-navy-dark">
            {de ? "Priorität" : "Priority"}
          </label>
          <select
            id="req-prio" data-testid="request-priority-select"
            value={form.priority}
            onChange={(e) => setForm({ ...form, priority: e.target.value })}
            className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-navy-dark"
          >
            {PRIORITIES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="req-date" className="block mb-2 text-[13px] font-medium text-navy-dark">
              {de ? "Wunschdatum" : "Preferred date"}{" "}
              <span className="text-navy-dark/50 font-normal">({de ? "optional" : "optional"})</span>
            </label>
            <input
              id="req-date" type="date"
              data-testid="request-date-input"
              value={form.date_needed}
              onChange={(e) => setForm({ ...form, date_needed: e.target.value })}
              className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-navy-dark"
            />
          </div>
          <div>
            <label htmlFor="req-time" className="block mb-2 text-[13px] font-medium text-navy-dark">
              {de ? "Wunschzeit" : "Preferred time"}{" "}
              <span className="text-navy-dark/50 font-normal">({de ? "optional" : "optional"})</span>
            </label>
            <input
              id="req-time" type="time"
              data-testid="request-time-input"
              value={form.preferred_time}
              onChange={(e) => setForm({ ...form, preferred_time: e.target.value })}
              className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-navy-dark"
            />
          </div>
        </div>

        <div>
          <label htmlFor="req-budget" className="block mb-2 text-[13px] font-medium text-navy-dark">
            {de ? "Budget" : "Budget"}{" "}
            <span className="text-navy-dark/50 font-normal">({de ? "optional" : "optional"})</span>
          </label>
          <input
            id="req-budget"
            data-testid="request-budget-input"
            value={form.budget}
            onChange={(e) => setForm({ ...form, budget: e.target.value })}
            placeholder="EUR"
            className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-navy-dark"
          />
        </div>

        <div className="bg-ivory/60 border border-navy/10 p-4 flex items-start gap-4" data-testid="request-approval-block">
          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-medium text-navy">
              {de ? "Freigabe erforderlich, bevor Kosten entstehen" : "Approval required before any costs"}
            </div>
            <div className="text-[12px] text-navy-dark/60 leading-relaxed mt-1">
              {de
                ? "Weissblom holt vorab Ihre Zustimmung zu jedem Angebot ein."
                : "Weissblom asks for your consent to every quote up front."}
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={form.requires_approval}
            data-testid="request-approval-toggle"
            onClick={() => setForm({ ...form, requires_approval: !form.requires_approval })}
            className="relative inline-flex h-7 w-12 shrink-0 rounded-full transition-colors border"
            style={{
              background: form.requires_approval ? BRAND_COLORS.navy : "#E7E1D3",
              borderColor: form.requires_approval ? BRAND_COLORS.navy : "#D4CAB0",
            }}
          >
            <span
              className="inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform"
              style={{ transform: form.requires_approval ? "translateX(22px)" : "translateX(3px)", marginTop: 2 }}
            />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <input ref={imgRef} type="file" accept={IMAGE_ACCEPT} multiple onChange={pickFiles} className="hidden" data-testid="request-image-input" />
          <input ref={docRef} type="file" accept={DOC_ACCEPT}   multiple onChange={pickFiles} className="hidden" data-testid="request-doc-input" />
          <button
            type="button"
            data-testid="request-attach-image-btn"
            disabled={uploading}
            onClick={() => imgRef.current?.click()}
            className="inline-flex items-center justify-center gap-2 border border-navy/20 hover:border-navy px-4 py-3 text-[13px] text-navy transition-colors disabled:opacity-50"
          >
            <ImagePlus className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
            {de ? "Bild anhängen" : "Attach image"}
          </button>
          <button
            type="button"
            data-testid="request-attach-doc-btn"
            disabled={uploading}
            onClick={() => docRef.current?.click()}
            className="inline-flex items-center justify-center gap-2 border border-navy/20 hover:border-navy px-4 py-3 text-[13px] text-navy transition-colors disabled:opacity-50"
          >
            <FileText className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
            {de ? "Dokument anhängen" : "Attach document"}
          </button>
        </div>

        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="request-attachment-list">
            {attachments.map((a) => (
              <div key={a.id} className="inline-flex items-center gap-2 bg-ivory border border-navy/15 pl-3 pr-2 py-1.5 text-[12px] text-navy-dark">
                {a.is_image ? <ImagePlus className="w-3.5 h-3.5" strokeWidth={1.6} /> : <FileText className="w-3.5 h-3.5" strokeWidth={1.6} />}
                <span className="truncate max-w-[180px]" title={a.name}>{a.name}</span>
                <button
                  type="button"
                  onClick={() => removeAttachment(a.id)}
                  data-testid={`request-attachment-remove-${a.id}`}
                  className="text-navy-dark/50 hover:text-red-700"
                  aria-label={de ? "Anhang entfernen" : "Remove attachment"}
                >
                  <X className="w-3.5 h-3.5" strokeWidth={2} />
                </button>
              </div>
            ))}
          </div>
        )}
      </form>

      <div className="mt-6 bg-white border border-navy/10 p-5 md:p-7" data-testid="popular-requests">
        <div className="label-caps mb-4">{de ? "Häufig angefragt" : "Frequently requested"}</div>
        <div className="flex flex-wrap gap-2">
          {POPULAR_PROMPTS.map((prompt) => (
            <button
              key={prompt}
              type="button"
              onClick={() => setForm((f) => ({ ...f, title: prompt }))}
              data-testid={`popular-${prompt.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
              className="border border-navy/15 hover:border-gold hover:bg-ivory/50 text-navy-dark px-4 py-2 text-[13px] rounded-full transition-colors"
            >
              {prompt}
            </button>
          ))}
        </div>
      </div>

      <div
        className="fixed left-0 right-0 z-40 border-t border-navy/10 md:hidden"
        style={{ background: BRAND_COLORS.navy, bottom: "72px" }}
        data-testid="request-sticky-footer"
      >
        <div className="max-w-[720px] mx-auto px-5 py-3.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-ivory/85 text-[12.5px]">
            <Sparkles className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.5} />
            {de ? "Sie buchen. Weissblom liefert." : "You ask. Weissblom delivers."}
          </div>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            data-testid="request-submit-btn-mobile"
            data-guest-locked={gate.isGuest ? "1" : "0"}
            className="inline-flex items-center gap-2 bg-ivory text-navy px-5 py-2.5 text-[12px] tracking-[0.15em] uppercase hover:bg-white transition-colors disabled:opacity-40"
          >
            {gate.isGuest && <Lock className="w-3.5 h-3.5" strokeWidth={1.6} />}
            {sending ? "…" : (de ? "Anfrage senden" : "Send request")}
          </button>
        </div>
      </div>

      <div className="hidden md:flex mt-6 items-center justify-between gap-4 px-6 py-4" style={{ background: BRAND_COLORS.navy }} data-testid="request-desktop-footer">
        <div className="flex items-center gap-2 text-ivory/85 text-[13px]">
          <Sparkles className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.5} />
          {de ? "Sie buchen. Weissblom liefert." : "You ask. Weissblom delivers."}
        </div>
        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit}
          data-testid="request-submit-btn"
          data-guest-locked={gate.isGuest ? "1" : "0"}
          className="inline-flex items-center gap-2 bg-ivory text-navy px-6 py-3 text-[12px] tracking-[0.15em] uppercase hover:bg-white transition-colors disabled:opacity-40"
        >
          {gate.isGuest && <Lock className="w-3.5 h-3.5" strokeWidth={1.6} />}
          {sending
            ? (de ? "Wird gesendet …" : "Sending …")
            : (de ? "Anfrage senden" : "Send request")}
        </button>
      </div>
      {gate.dialog}
    </div>
  );
}
